import { useEffect, useState } from "react";
import MarkdownEditor from '@uiw/react-markdown-editor';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PoProject } from "../../../../api/__gen__/data-contracts";
import { BiHelpCircle, BiSync, BiCloud } from "react-icons/bi";
import { ImInfo } from "react-icons/im";
import { Select, Tag } from "antd";
import ImagePicker from "../../../image-upload/ImagePicker";

const ProjectOverview = ({ projectDetail, isEditMode, onSave, onCancel }: {
  projectDetail: PoProject,
  isEditMode: boolean,
  onSave?: (data: PoProject) => void
  onCancel?: () => void
}) => {
  const [projectMetadata, setProjectMetadata] = useState<PoProject>({})

  useEffect(() => {
    setProjectMetadata(projectDetail)

  }, [projectDetail])

  const handleSave = () => {
    if (onSave) {
      onSave(projectMetadata)
    }
  }

  return <div className="py-6 px-16 overflow-auto w-full max-w-[1200px]">
    <div id="overview" className="flex-1">
      <div className="mb-6">
        <img src={projectMetadata?.image || "https://picsum.photos/seed/picsum/80/80"} alt="" className="w-[80px] h-[80px] object-cover rounded" />
      </div>
      <div className="mb-6">
        <h2 className="text-3xl font-semibold">
          {isEditMode ? <input type="text" className="w-2/3 border p-2" value={projectMetadata.name} onChange={(e) => {
            setProjectMetadata({
              ...projectMetadata,
              name: e.target.value
            })
          }} /> : projectDetail?.name || 'API Project Name'}
        </h2>
        <div className="mt-2 w-1/2">
          {
            !isEditMode ? <div className="flex gap-2">
              {projectDetail?.chains?.map(e => {
                return <Tag color="blue" className="cursor-pointer" key={e}>
                  {e}
                </Tag>
              })}
            </div> : <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Chains"
              onChange={(e) => {
                setProjectMetadata({
                  ...projectMetadata,
                  chains: e
                })
              }}
              value={projectMetadata.chains || []}
              options={projectMetadata.chains?.map(e => {
                return {
                  label: e,
                  value: e
                }
              })}
            />
          }

        </div>
        <div className="mt-2 w-1/2">
          {
            !isEditMode ? <div className="flex gap-2">
              {projectDetail?.labels?.map(e => {
                return <Tag className="cursor-pointer" key={e}>
                  {e}
                </Tag>
              })}
            </div> : <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Labels"
              value={projectMetadata.labels || []}
              onChange={(e) => {
                setProjectMetadata({
                  ...projectMetadata,
                  labels: e
                })
              }}
              options={projectMetadata.labels?.map(e => {
                return {
                  label: e,
                  value: e
                }
              })}
            />
          }

        </div>
      </div>
      <div className="flex justiy-between gap-8">
        <div className="mb-6 flex-1 opacity-80" data-color-mode="light">
          <div className="my-3">
            {
              isEditMode ? <input placeholder="Excerpt" className="p-2 border outline-none w-full h-12" value={projectMetadata.detail} onChange={(e) => {
                setProjectMetadata({
                  ...projectMetadata,
                  detail: e.target.value
                })
              }
              } /> : <div className="text-[#999ca0]">
                {projectMetadata.detail}
              </div>
            }
          </div>
          <div className="my-3">
            {
              isEditMode ?
                <ImagePicker
                  value={projectMetadata.image || ""}
                  onChange={(e) => {
                    setProjectMetadata({
                      ...projectMetadata,
                      image: e
                    })
                  }}
                /> : null
            }
          </div>
          {
            isEditMode ?

              <MarkdownEditor
                basicSetup={{
                  lineNumbers: false,
                }}
                minHeight="300px"
                maxHeight="600px"
                height="100%"
                theme='light'
                maxWidth="100%"
                value={projectMetadata.overview || ''} onChange={(e) => setProjectMetadata({
                  ...projectMetadata,
                  overview: e
                })} />
              : <ReactMarkdown
                remarkPlugins={[remarkGfm]}
              // className={styles.setupMarkdown}
              >
                {projectMetadata.overview || 'API Project Overview'}
              </ReactMarkdown>
          }
          {
            isEditMode && <div className="mt-6 flex gap-2 justify-end">
              <button className="bg-primary bg-[#a3a3a3] text-[#fff] px-3 py-2 rounded" onClick={() => {
                onCancel?.()
                setProjectMetadata(projectDetail)
              }}>Cancel</button>
              <button className="bg-primary text-[#fff] px-3 py-2 rounded" onClick={handleSave}>Save</button>
            </div>
          }
        </div>
        <div className="max-w-[320px] min-w-[300px]">
          {/* help */}
          <div className="mb-6 text-[#999ca0]">
            <div className="flex gap-2 items-center">
              <BiHelpCircle />
              <div>Help</div>
            </div>
            <div className="mt-3 pl-6">
              <div className="text-sm text-[#999ca0] mb-2">
                Contact Support
              </div>
              <div className="text-sm text-[#999ca0] mb-2">
                Document
              </div>
              <div className="text-sm text-[#999ca0] mb-2">
                Contact Us
              </div>
            </div>
          </div>
          {/* Refreshes */}
          <div className="mb-6 text-[#999ca0]">
            <div className="flex gap-2 items-center">
              <BiSync />
              <div>Refreshes</div>
            </div>
            <div className="mt-3 pl-6">
              <div className="text-sm text-[#999ca0] mb-2">
                Daily
              </div>
            </div>
          </div>

          <div className="mb-6 text-[#999ca0]">
            <div className="flex gap-2 items-center">
              <BiCloud />
              <div>Cloud Region Availability</div>
            </div>
            <div className="mt-3 pl-6">
              <div className="text-sm text-[#999ca0] mb-2">
                AWS US West (Oregon)
              </div>
            </div>
          </div>

          <div className="mb-6 text-[#999ca0]">
            <div className="flex gap-2 items-center">
              <ImInfo />
              <div>About ZTBL</div>
            </div>
            <div className="mt-3 pl-6">
              <div className="text-sm text-[#999ca0] mb-2">
                Lorem ipsum dolor sit, amet consectetur adipisicing elit. Ipsam sunt debitis nesciunt tempora esse quo error, nobis iure ratione iusto non ab, voluptas asperiores quod? Eveniet, ducimus? Ea, similique earum.
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>


  </div>
}

export default ProjectOverview;